import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { LegalEntitiesPlatformAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type LegalEntitiesPlatformAccessJSON = z.infer<
  typeof LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessJSONZ
>;

export type LegalEntitiesPlatformAccessParams = z.infer<
  typeof LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessZ
>;

export default class LegalEntitiesPlatformAccessSchema {
  public static readonly LegalEntitiesPlatformAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.LegalEntitiesPlatformAccess),
    accessType: z.enum(LegalEntitiesPlatformAccessType),
    scope: z.enum(Scope),
  });

  public static readonly LegalEntitiesPlatformAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.LegalEntitiesPlatformAccess),
    accessType: z.enum(LegalEntitiesPlatformAccessType),
    scope: z.enum(Scope),
  });

  public static validate(values: unknown): LegalEntitiesPlatformAccessParams {
    return parseOrThrow(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessZ, values, {
      logError: true,
      schemaName: "LegalEntitiesPlatformAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): LegalEntitiesPlatformAccessJSON {
    return parseOrThrow(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessJSONZ, values, {
      logError: true,
      schemaName: "LegalEntitiesPlatformAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is LegalEntitiesPlatformAccessParams {
    try {
      LegalEntitiesPlatformAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is LegalEntitiesPlatformAccessJSON {
    try {
      LegalEntitiesPlatformAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: LegalEntitiesPlatformAccessJSON): LegalEntitiesPlatformAccessParams {
    const parsed = parseOrThrow(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessJSONZ, data, {
      logError: true,
      schemaName: "LegalEntitiesPlatformAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return LegalEntitiesPlatformAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: LegalEntitiesPlatformAccessParams): LegalEntitiesPlatformAccessJSON {
    const parsed = LegalEntitiesPlatformAccessSchema.validate(notification);
    return LegalEntitiesPlatformAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessJSONZ,
    LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.LegalEntitiesPlatformAccess as const,
        accessType: data.accessType ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.LegalEntitiesPlatformAccess as const,
        accessType: resource.accessType,
        scope: resource.scope,
      }),
    },
  );
}
